import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import { isCustomerCloudSourcePageAtom } from '@/pages/Administration/Devices/Devices.store';

import useCloudSourceForm from '../../CloudSourceHooks/useCloudSourceForm.hooks';
import SOURCE_TYPE from '../../SourceCreate.constants';
import { limaDevicesAtom } from '../../SourceCreate.store';
import { getLimaDeviceForCustomerSource } from '../utils/deviceSelection.utils';

/**
 * Auto-populate device_uid and ingest_from fields on customer cloud source page.
 *
 * Business Rules:
 * - Restricted orgs: Use lima-aws-0r device
 * - Azure sources: Use lima-azure-01 device
 * - Other sources: Use lima-aws-01 device
 * - Microsoft sources: Set ingest_from for all 4 data providers
 * - Other sources: Set single ingest_from field
 */
export const useCustomerCloudAutoFill = () => {
  const { setValue } = useFormContext();

  const [limaDevices] = useAtom(limaDevicesAtom);
  const [isCustomerCloudSourcePage] = useAtom(isCustomerCloudSourcePageAtom);

  const { selectedOrgData, selectedSourceTypeId } = useCloudSourceForm();

  useEffect(() => {
    if (!isCustomerCloudSourcePage) return;
    if (selectedSourceTypeId <= 0) return;

    // Step 1: Auto-select Lima device based on org and source type
    const deviceUid = getLimaDeviceForCustomerSource(
      selectedSourceTypeId,
      selectedOrgData?.is_restricted || false,
      limaDevices || []
    );

    setValue('device_uid', deviceUid, {
      shouldValidate: true,
      shouldDirty: true
    });

    // Step 2: Auto-set ingest_from date
    const currentDate = dayjs().format(YEAR_MONTH_DAY_TIME_FORMAT);

    if (selectedSourceTypeId === SOURCE_TYPE.MICROSOFT) {
      // Microsoft sources have multiple data providers that each need ingest_from
      const microsoftDataProviders = [
        'data_provider.MicrosoftEntraID.GraphAPI',
        'data_provider.MicrosoftEntraID.O365API',
        'data_provider.O365',
        'data_provider.MicrosoftDefenderUnified'
      ];

      setValue('common_ingest_from', currentDate, {
        shouldValidate: true,
        shouldDirty: true
      });

      microsoftDataProviders.forEach((providerPath) => {
        setValue(`${providerPath}.ingest_from`, currentDate, {
          shouldValidate: true,
          shouldDirty: true
        });
      });
    } else {
      // Standard sources have single ingest_from field
      setValue('ingest_from', currentDate, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [
    isCustomerCloudSourcePage,
    selectedOrgData,
    selectedSourceTypeId,
    limaDevices,
    setValue
  ]);
};
